import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import {
    faBoxArchive,
    faCircleCheck,
    faFileInvoiceDollar,
    faLockOpen,
    faSpinner,
} from '@fortawesome/pro-light-svg-icons';
import { BatchPackageState, ReturnStatusType } from 'returnCenter-api';
import useAppDispatch from 'hooks/useAppDispatch';
import useAppSelector from 'hooks/useAppSelector';
import useBatchPackages from 'hooks/useBatchPackages';
import {
    processReturns,
    closeRemainingAsMissing,
    completeBatch,
    creditReturnSlips,
    reopenBatch,
    clearCreditSelection,
    selectCreditSelection,
    selectProcessingSummaryLoading,
    selectProcessingCompanyCode,
    selectCurrentBatchReturn,
} from 'store/reducers/batchReturnSlice';
import { selectCreditsBatchSlipsTogether } from 'store/reducers/configSlice';
import { CompanyProcessingGroup } from 'types/batchReturnProcessing';
import Flex from 'components/ui/Flex';
import Text from 'components/ui/Text';
import ActionButton from 'components/ui/ActionButton';
import Spinner from 'components/ui/Spinner';
import ConfirmationDialog from 'components/ui/ConfirmationDialog';
import ReopenBatchDialog from 'components/batch-returns/ReopenBatchDialog';

interface BatchReturnProcessingPanelProps {
    batchReturnId: number;
}

const toastOptions = { autoClose: 3500, closeOnClick: true };

/**
 * Progress per receiving company, and the actions that span packages: crediting the ticked ones,
 * retrying a failed credit, creating the remaining slips and closing what never arrived. The
 * packages themselves, and what can be done with each, are in the list beside it.
 */
const BatchReturnProcessingPanel = ({ batchReturnId }: BatchReturnProcessingPanelProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { summary, batchCompleted, isOwnCompany, refresh, showError } = useBatchPackages();
    const loading = useAppSelector(selectProcessingSummaryLoading);
    const processing = useAppSelector(selectProcessingCompanyCode);
    const batchReturn = useAppSelector(selectCurrentBatchReturn);
    const creditSelection = useAppSelector(selectCreditSelection);
    // A company crediting each slip as it completes credits nothing from here.
    const creditsTogether = useAppSelector(selectCreditsBatchSlipsTogether);

    const [closeRemainingCompany, setCloseRemainingCompany] = useState<CompanyProcessingGroup | null>(null);
    const [processCompany, setProcessCompany] = useState<CompanyProcessingGroup | null>(null);
    const [confirmComplete, setConfirmComplete] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [confirmReopen, setConfirmReopen] = useState(false);
    const [reopening, setReopening] = useState(false);
    const [confirmCredit, setConfirmCredit] = useState<number[] | null>(null);
    const [crediting, setCrediting] = useState(false);

    // Every package nobody has made a slip for and that is not missing: the backend takes exactly
    // these rows, so packages already started are never put on a second slip.
    const remainingPackages = (company: CompanyProcessingGroup) =>
        company.packages.filter((p) => p.state === BatchPackageState.NotStarted).map((p) => p.sourceReturnId);

    const handleConfirmProcess = useCallback(async () => {
        const company = processCompany;
        setProcessCompany(null);
        if (!company) return;
        const returnIds = remainingPackages(company);
        if (returnIds.length === 0) return;

        const result = await dispatch(processReturns({ batchReturnId, returnIds }));
        if (processReturns.fulfilled.match(result)) {
            toast.success(
                t('batchReturn.remainingSlipCreated', 'Return slip #{{id}} created for {{count}} packages.', {
                    id: result.payload.createdReturnId,
                    count: returnIds.length,
                }),
                toastOptions,
            );
        } else {
            showError();
        }
        refresh();
    }, [dispatch, batchReturnId, processCompany, refresh, showError, t]);

    const handleConfirmCredit = useCallback(async () => {
        const returnSlipIds = confirmCredit ?? [];
        setConfirmCredit(null);
        if (returnSlipIds.length === 0) return;

        setCrediting(true);
        const result = await dispatch(creditReturnSlips({ batchReturnId, returnSlipIds }));
        setCrediting(false);

        if (creditReturnSlips.fulfilled.match(result)) {
            const { failedReturnIds, creditNoteNumbers, reference } = result.payload;
            if (failedReturnIds.length > 0) {
                toast.warning(
                    t(
                        'batchReturn.creditFailed',
                        'The credit failed for {{count}} packages. See the return log and try again.',
                        { count: failedReturnIds.length },
                    ),
                    { autoClose: 6000, closeOnClick: true },
                );
            } else {
                toast.success(
                    t('batchReturn.credited', 'Credited with reference {{reference}}: {{creditNotes}}', {
                        reference,
                        creditNotes: creditNoteNumbers.join(', '),
                    }),
                    toastOptions,
                );
            }
            dispatch(clearCreditSelection());
        } else {
            showError(result.payload?.message);
        }
        refresh();
    }, [dispatch, batchReturnId, confirmCredit, refresh, showError, t]);

    const handleConfirmComplete = useCallback(async () => {
        setConfirmComplete(false);
        setCompleting(true);
        const result = await dispatch(completeBatch(batchReturnId));
        setCompleting(false);

        if (completeBatch.fulfilled.match(result)) {
            toast.success(
                t('batchReturn.batchCompleted', 'Batch {{id}} is completed.', { id: batchReturnId }),
                toastOptions,
            );
        } else {
            showError(result.payload?.message);
        }
        refresh();
    }, [dispatch, batchReturnId, refresh, showError, t]);

    const handleConfirmReopen = useCallback(
        async (comment: string) => {
            setReopening(true);
            const result = await dispatch(reopenBatch({ batchReturnId, comment }));
            setReopening(false);
            setConfirmReopen(false);

            if (reopenBatch.fulfilled.match(result)) {
                toast.success(
                    t('batchReturn.batchReopened', 'Batch {{id}} is open again.', { id: batchReturnId }),
                    toastOptions,
                );
            } else {
                showError(result.payload?.message);
            }
            refresh();
        },
        [dispatch, batchReturnId, refresh, showError, t],
    );

    const handleConfirmCloseRemaining = useCallback(async () => {
        setCloseRemainingCompany(null);
        const result = await dispatch(closeRemainingAsMissing(batchReturnId));

        if (closeRemainingAsMissing.fulfilled.match(result)) {
            const openSlips = result.payload.openReturnSlipIds.length;
            if (openSlips > 0) {
                toast.info(
                    t(
                        'batchReturn.openSlipsRemain',
                        '{{count}} packages have a return slip that is not completed yet.',
                        {
                            count: openSlips,
                        },
                    ),
                    toastOptions,
                );
            }
        } else {
            showError(result.payload?.message);
        }
        refresh();
    }, [dispatch, batchReturnId, refresh, showError, t]);

    if (loading && !summary) {
        return (
            <Panel className="p-3" column align="center">
                <Spinner color="text" size="small" />
            </Panel>
        );
    }

    if (!summary || summary.companies.length === 0 || !batchReturn) {
        return null;
    }

    // The batch is completed by hand, by a receiving company, once every row of every company is
    // credited or missing: the same row counts the server checks. What still stands in the way is
    // listed under the button.
    const isReceiver = summary.companies.some(isOwnCompany);
    const canComplete = !batchCompleted && isReceiver;
    const outstanding = summary.companies
        .map((company) => ({ companyCode: company.companyCode, count: company.totalRows - company.resolvedRows }))
        .filter((c) => c.count > 0);
    const sumRows = (rows: (company: CompanyProcessingGroup) => number) =>
        summary.companies.reduce((sum, company) => sum + rows(company), 0);
    const processRemaining = processCompany ? remainingPackages(processCompany) : [];

    return (
        <Panel className="p-3" column gap="0.75rem">
            <SectionLabel>{t('batchReturn.processingProgress', 'Processing')}</SectionLabel>
            {summary.companies.map((company) => {
                const canAct = isOwnCompany(company) && !batchCompleted;
                const canProcess = canAct && company.unprocessedRows > 0;
                const isProcessing = processing != null;
                const isCompleted = company.totalRows > 0 && company.resolvedRows === company.totalRows;
                const notStartedCount = remainingPackages(company).length;
                // A failed round is retried as it was, before anything else is credited.
                const slipsIn = (status: ReturnStatusType) =>
                    company.packages
                        .filter((p) => p.returnSlipId != null && p.returnSlipStatus === status)
                        .map((p) => p.returnSlipId!);
                const failedRound = slipsIn(ReturnStatusType.CreditError);
                const creditable = slipsIn(ReturnStatusType.BatchHandled);
                const selected = creditable.filter((id) => creditSelection.includes(id));
                const canCredit = canAct && creditsTogether && failedRound.length === 0 && creditable.length > 0;

                return (
                    <CompanyRow key={company.companyCode} column gap="0.25rem">
                        <Flex justify="between" align="center">
                            <Text bold fontSize={12}>
                                {company.companyCode}
                            </Text>
                            <Text fontSize={11} color="textLight">
                                {t('batchReturn.resolved', '{{resolved}}/{{total}} done', {
                                    resolved: company.resolvedRows,
                                    total: company.totalRows,
                                })}
                            </Text>
                        </Flex>
                        <ProgressBar>
                            <ProgressFill $percent={(company.resolvedRows / company.totalRows) * 100} />
                        </ProgressBar>
                        <Flex justify="between" align="center">
                            <Text fontSize={11} color="textLight">
                                {t(
                                    'batchReturn.handledCreditedMissing',
                                    '{{handled}} handled · {{credited}} credited · {{missing}} missing',
                                    {
                                        handled: company.handledRows,
                                        credited: company.creditedRows,
                                        missing: company.missingRows,
                                    },
                                )}
                            </Text>
                            {company.deviationRows > 0 && (
                                <DeviationBadge fontSize={11} bold>
                                    {t('batchReturn.deviations', '{{count}} deviations', {
                                        count: company.deviationRows,
                                    })}
                                </DeviationBadge>
                            )}
                        </Flex>

                        {canCredit && (
                            <ActionButton
                                label={t('batchReturn.creditSelected', 'Credit selected ({{count}})', {
                                    count: selected.length,
                                })}
                                icon={crediting ? faSpinner : faFileInvoiceDollar}
                                onClick={() => setConfirmCredit(selected)}
                                disabled={crediting || selected.length === 0}
                                isLoading={crediting}
                                main
                                fullWidth
                            />
                        )}
                        {canAct && creditsTogether && failedRound.length > 0 && (
                            <ActionButton
                                label={t('batchReturn.retryCredit', 'Retry failed credit ({{count}})', {
                                    count: failedRound.length,
                                })}
                                icon={crediting ? faSpinner : faFileInvoiceDollar}
                                onClick={() => setConfirmCredit(failedRound)}
                                disabled={crediting}
                                isLoading={crediting}
                                main
                                fullWidth
                            />
                        )}
                        {canProcess && (
                            <ActionButton
                                label={t(
                                    'batchReturn.createRemainingSlip',
                                    'Create return slip for remaining ({{count}})',
                                    {
                                        count: notStartedCount,
                                    },
                                )}
                                icon={isProcessing ? faSpinner : faFileInvoiceDollar}
                                onClick={() => setProcessCompany(company)}
                                disabled={isProcessing}
                                isLoading={isProcessing}
                                fullWidth
                            />
                        )}
                        {canProcess && (
                            <ActionButton
                                label={t('batchReturn.closeRemaining', 'Close batch – remaining missing')}
                                icon={faBoxArchive}
                                onClick={() => setCloseRemainingCompany(company)}
                                disabled={isProcessing}
                                fullWidth
                            />
                        )}
                        {isCompleted && !batchCompleted && (
                            <CompletedBadge align="center" gap="0.25rem">
                                <Text fontSize={11} color="success" bold>
                                    {t('batchReturn.companyResolved', 'All credited or missing')}
                                </Text>
                            </CompletedBadge>
                        )}

                        <ConfirmationDialog
                            open={confirmCredit != null && canAct}
                            onClose={() => setConfirmCredit(null)}
                            title={t('batchReturn.confirmCredit', 'Credit packages')}
                            content={t(
                                'batchReturn.confirmCreditMessage',
                                'One credit note is created for {{count}} packages, with reference Batch {{id}}.',
                                { count: confirmCredit?.length ?? 0, id: batchReturnId },
                            )}
                            onCancel={() => setConfirmCredit(null)}
                            onConfirm={handleConfirmCredit}
                        />

                        <ConfirmationDialog
                            open={closeRemainingCompany?.companyCode === company.companyCode}
                            onClose={() => setCloseRemainingCompany(null)}
                            title={t('batchReturn.confirmCloseRemaining', 'Close remaining packages')}
                            content={t(
                                'batchReturn.confirmCloseRemainingMessage',
                                'All {{count}} packages without a return slip will be marked missing.',
                                { count: notStartedCount },
                            )}
                            onCancel={() => setCloseRemainingCompany(null)}
                            onConfirm={handleConfirmCloseRemaining}
                        />
                    </CompanyRow>
                );
            })}

            {batchReturn.reopenedAt && (
                <Text fontSize={11} color="textLight">
                    {t('batchReturn.reopenedInfo', 'Reopened {{date}} by {{user}}: {{comment}}', {
                        date: new Date(batchReturn.reopenedAt).toLocaleString(),
                        user: batchReturn.reopenedBy,
                        comment: batchReturn.reopenComment,
                    })}
                </Text>
            )}

            {batchCompleted && isReceiver && (
                <ActionButton
                    label={t('batchReturn.reopenBatch', 'Reopen batch')}
                    icon={reopening ? faSpinner : faLockOpen}
                    onClick={() => setConfirmReopen(true)}
                    disabled={reopening}
                    isLoading={reopening}
                    fullWidth
                />
            )}

            <ReopenBatchDialog
                open={confirmReopen}
                batchId={batchReturnId}
                message={t(
                    'batchReturn.reopenMessage',
                    'The batch goes back to partially processed, so a package that turned up can be taken in and credited. Complete it again afterwards.',
                )}
                busy={reopening}
                onCancel={() => setConfirmReopen(false)}
                onConfirm={handleConfirmReopen}
            />

            {canComplete && (
                <Flex column gap="0.25rem">
                    <ActionButton
                        label={t('batchReturn.completeBatch', 'Complete batch')}
                        icon={completing ? faSpinner : faCircleCheck}
                        onClick={() => setConfirmComplete(true)}
                        disabled={completing || outstanding.length > 0}
                        isLoading={completing}
                        main
                        fullWidth
                    />
                    {outstanding.map((c) => (
                        <Text key={c.companyCode} fontSize={11} color="textLight">
                            {t(
                                'batchReturn.packagesOutstanding',
                                '{{company}}: {{count}} rows are neither credited nor missing',
                                { company: c.companyCode, count: c.count },
                            )}
                        </Text>
                    ))}
                </Flex>
            )}

            <ConfirmationDialog
                open={processCompany != null}
                onClose={() => setProcessCompany(null)}
                title={t('batchReturn.confirmCreateRemaining', 'Create one return slip for the remaining packages')}
                content={t(
                    'batchReturn.confirmCreateRemainingMessage',
                    'One return slip is created with all {{count}} packages that have no return slip yet and are not marked missing: {{packages}}. Packages that already have a return slip are left as they are. To handle the packages one at a time, scan them or use Create on each package instead.',
                    {
                        count: processRemaining.length,
                        packages: processRemaining.map((id) => `#${id}`).join(', '),
                    },
                )}
                onCancel={() => setProcessCompany(null)}
                onConfirm={handleConfirmProcess}
            />

            <ConfirmationDialog
                open={confirmComplete}
                onClose={() => setConfirmComplete(false)}
                title={t('batchReturn.confirmCompleteBatch', 'Complete batch {{id}}', { id: batchReturnId })}
                content={t(
                    'batchReturn.confirmCompleteBatchMessage',
                    '{{credited}} rows credited · {{missing}} missing · {{deviations}} deviations. The batch cannot be changed after this.',
                    {
                        credited: sumRows((c) => c.creditedRows),
                        missing: sumRows((c) => c.missingRows),
                        deviations: sumRows((c) => c.deviationRows),
                    },
                )}
                onCancel={() => setConfirmComplete(false)}
                onConfirm={handleConfirmComplete}
            />
        </Panel>
    );
};

export default BatchReturnProcessingPanel;

const Panel = styled(Flex)`
    background-color: ${({ theme }) => theme.colors.fillWhite};
    border-radius: var(--radius);
    border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const SectionLabel = styled(Text)`
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: ${({ theme }) => theme.colors.textLight};
`;

const CompanyRow = styled(Flex)`
    padding: 0.5rem 0;
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
    &:last-child {
        border-bottom: none;
    }
`;

const ProgressBar = styled.div`
    height: 4px;
    background-color: ${({ theme }) => theme.colors.borderLight};
    border-radius: 2px;
    overflow: hidden;
`;

const ProgressFill = styled.div<{ $percent: number }>`
    height: 100%;
    width: ${({ $percent }) => $percent}%;
    background-color: ${({ theme }) => theme.colors.success};
    transition: width 0.3s ease;
`;

const DeviationBadge = styled(Text)`
    padding: 0 0.375rem;
    border-radius: 2px;
    background-color: ${({ theme }) => theme.colors.warning};
    color: ${({ theme }) => theme.colors.text};
`;

const CompletedBadge = styled(Flex)`
    padding: 0.25rem 0;
`;
